import * as React from "react";
import { Modal, Button, Form } from "semantic-ui-react";
import { CoachesServices } from "@/services/bedrock/CoachesServices";
import { validEmail } from "@/utils";

interface OwnProps {
  open: boolean;
  onNew: () => void;
  onClose: () => void;
}

const initialFormState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  companyName: "",
  title: "",
  password: "",
  isDemo: false,
};

// Regex requires: 1 lower, 1 upper, 1 number, 1 special, 8+ chars
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+=[{\]};:<>|./?,-]).{8,}$/;

const NewCoachModalFC: React.FC<OwnProps> = ({ open, onClose, onNew }) => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [formData, setFormData] = React.useState(initialFormState);
  const [formError, setFormError] = React.useState<string | null>(null);

  const handleChange = (e: React.FormEvent<HTMLInputElement>, { name, value, checked, type }: any) => {
    const newValue = type === "checkbox" ? checked : value;

    setFormData(prev => ({ ...prev, [name]: newValue }));

    if (name === "password" || (name == "email" && formError)) {
      setFormError(null);
    }
  };

  const createCoach = async () => {
    if (!validEmail(formData.email)) {
      setFormError("Invalid email address");
      return;
    }
    if (!PASSWORD_REGEX.test(formData.password)) {
      setFormError(
        "Password must be at least 8 characters and include 1 uppercase, 1 lowercase, 1 number, and 1 special character."
      );
      return;
    }

    setLoading(true);
    try {
      await CoachesServices.createCoach(formData);
      setFormData(initialFormState);
      setFormError(null);
      onNew();
    } catch (error) {
      console.error("Failed to create coach:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(initialFormState);
    setFormError(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} closeIcon={true} className="newCoachModal" size="small">
      <Modal.Header>New Coach</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Group widths="equal">
            <Form.Input
              fluid
              label="First Name"
              name="firstName"
              placeholder="First Name"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
            <Form.Input
              fluid
              label="Last Name"
              name="lastName"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </Form.Group>
          <Form.Input
            label="Email"
            name="email"
            type="email"
            placeholder="Email Address"
            value={formData.email}
            onChange={handleChange}
            required
          />
          <Form.Input
            label="Phone"
            name="phone"
            type="tel"
            placeholder="Phone Number"
            value={formData.phone}
            onChange={handleChange}
          />
          <Form.Group widths="equal">
            <Form.Input
              fluid
              label="Company Name"
              name="companyName"
              placeholder="Company Name"
              value={formData.companyName}
              onChange={handleChange}
            />
            <Form.Input
              fluid
              label="Title"
              name="title"
              placeholder="Job Title"
              value={formData.title}
              onChange={handleChange}
            />
          </Form.Group>
          <Form.Input
            label="Password"
            name="password"
            type="password"
            placeholder="Temporary Password"
            value={formData.password}
            onChange={handleChange}
            required
            error={formError ? { content: formError, pointing: "above" } : false}
          />
          <Form.Checkbox
            label="Is Demo Account?"
            name="isDemo"
            type="checkbox"
            checked={formData.isDemo}
            onChange={handleChange}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          data-elm-id="admin-dashboard-modal-coach-permission-save-btn"
          primary
          onClick={createCoach}
          content="Create New Coach"
          loading={loading}
          disabled={!formData.firstName || !formData.lastName || !formData.email || !formData.password}
        />
      </Modal.Actions>
    </Modal>
  );
};

export const NewCoachModal = NewCoachModalFC;
